import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { useWizardFlow, type UseWizardFlowOptions, type WizardFlow, type WizardStepDef } from '@klear/ui/wizard/flow'

export function useFlowWizardNav(
  onNavStateChange: (nav: WizardStepGroupNavState) => void,
  flow: Pick<WizardFlow, 'steps' | 'activeStepId' | 'onStepSelect' | 'isStepDisabled'>
): void {
  const stepsKey = useMemo(() => flow.steps.map((s) => `${s.id}:${s.label}:${s.section ?? ''}`).join(','), [flow.steps])
  const reportedSteps = useMemo(
    () => flow.steps.map((s) => ({ id: s.id, label: s.label, section: s.section })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- identity of flow.steps is unstable
    [stepsKey]
  )

  const selectRef = useRef(flow.onStepSelect)
  selectRef.current = flow.onStepSelect
  const disabledRef = useRef(flow.isStepDisabled)
  disabledRef.current = flow.isStepDisabled
  const hasSelect = flow.onStepSelect != null
  const hasDisabled = flow.isStepDisabled != null

  const stableSelect = useCallback((stepId: string) => {
    selectRef.current?.(stepId)
  }, [])
  const stableDisabled = useCallback((stepId: string) => disabledRef.current?.(stepId) ?? false, [])

  useEffect(() => {
    onNavStateChange({
      steps: reportedSteps,
      activeStepId: flow.activeStepId,
      onStepSelect: hasSelect ? stableSelect : undefined,
      isStepDisabled: hasDisabled ? stableDisabled : undefined
    })
  }, [
    stepsKey,
    reportedSteps,
    flow.activeStepId,
    hasSelect,
    hasDisabled,
    stableSelect,
    stableDisabled,
    onNavStateChange
  ])
}

export type UseReportedWizardFlowOptions = UseWizardFlowOptions & {
  onNavStateChange: (nav: WizardStepGroupNavState) => void
}

export function useReportedWizardFlow(stepDefs: WizardStepDef[], options: UseReportedWizardFlowOptions): WizardFlow {
  const { onNavStateChange, ...flowOptions } = options
  const flow = useWizardFlow(stepDefs, flowOptions)
  useFlowWizardNav(onNavStateChange, flow)
  return flow
}

export type UsePathSyncedWizardFlowOptions = UseReportedWizardFlowOptions & {
  pathStepId: string | undefined
  resolveInitialStepId: (pathStepId: string | undefined) => string
  syncPathStepId: (stepId: string) => void
}

export function usePathSyncedWizardFlow(
  stepDefs: WizardStepDef[],
  options: UsePathSyncedWizardFlowOptions
): WizardFlow {
  const { pathStepId, resolveInitialStepId, syncPathStepId, ...reportedOptions } = options
  const [initialStepId] = useState(() => resolveInitialStepId(pathStepId))
  const flow = useReportedWizardFlow(stepDefs, { ...reportedOptions, initialStepId })

  const skipPathAdoptionRef = useRef(false)

  useEffect(() => {
    if (pathStepId === flow.activeStepId) return
    skipPathAdoptionRef.current = true
    syncPathStepId(flow.activeStepId)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pathStepId is read for equality only; URL→flow is the sibling effect
  }, [flow.activeStepId, syncPathStepId])

  useEffect(() => {
    if (pathStepId == null || pathStepId === '' || pathStepId === flow.activeStepId) return
    if (skipPathAdoptionRef.current) {
      skipPathAdoptionRef.current = false
      return
    }
    if (flow.isStepDisabled?.(pathStepId) === true) {
      skipPathAdoptionRef.current = true
      syncPathStepId(flow.activeStepId)
      return
    }
    flow.setActiveStepId(pathStepId)
  }, [pathStepId, flow.activeStepId, flow.setActiveStepId, flow.isStepDisabled, syncPathStepId])

  return flow
}
